
import React, { useContext } from 'react';
import { SettingsSearchContext } from './SettingsContext';
import { InfoPopup } from './InfoPopup';
import { SETTING_INFO } from './settingInfo';

interface ControlGroupProps {
  label: string;
  children?: React.ReactNode;
  value?: string | number;
  tooltip?: string;
  settingKey?: string;
  disabled?: boolean;
  disabledMessage?: string;
  modes?: string[];
  className?: string;
  darkMode?: boolean;
}

export const ControlGroup: React.FC<ControlGroupProps> = ({ 
  label, children, value, tooltip, settingKey, disabled, disabledMessage, modes, className, darkMode 
}) => {
  const searchQuery = useContext(SettingsSearchContext);

  // Automatically fetch info from SETTING_INFO if settingKey is provided
  const info = settingKey ? SETTING_INFO[settingKey] : null;
  const finalTooltip = tooltip || info?.description || '';
  const finalModes = modes || info?.modes;

  if (searchQuery && !label.toLowerCase().includes(searchQuery.toLowerCase())) {
    return null;
  }

  // Detect if children is just a Toggle for inline layout
  const isToggle = React.Children.count(children) === 1 && 
                   React.isValidElement(children) && 
                   (children.type as any).name === 'Toggle';

  return (
    <div className={`flex ${isToggle ? 'flex-row items-center justify-between' : 'flex-col items-start gap-2.5'} w-full py-2.5 transition-all duration-300 min-w-0 ${disabled ? 'opacity-30 pointer-events-none grayscale' : ''} ${className || ''}`}>
      <div className="flex flex-col gap-1 min-w-0 w-full overflow-hidden">
        <div className="flex items-center justify-between gap-3 w-full min-w-0">
          <label className="text-[13px] font-bold opacity-100 flex-1 break-words leading-tight">{label}</label>
          {(finalTooltip || (disabled && disabledMessage)) && (
            <InfoPopup 
              content={finalTooltip} 
              disabled={disabled} 
              disabledMessage={disabledMessage} 
              modes={finalModes}
              darkMode={darkMode}
            />
          )}
        </div>
        {value !== undefined && <span className="text-[10px] font-mono font-bold opacity-30 break-all tracking-tighter">{value}</span>}
      </div>
      <div className={`w-full ${isToggle ? 'max-w-[80px] flex justify-end' : ''}`}>
        {children}
      </div>
    </div>
  );
};
